import React, { useState, useMemo, useContext } from 'react';
import { Card, Form, Alert, Spinner, Container } from 'react-bootstrap';
import { ExclamationTriangleFill } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import { useResetPassword } from '../hooks/useBackend';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { validatePasswordComplexity } from '../utils/password';
import { AuthContext } from '../contexts/AuthContext';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import LandingNav from '../components/shared/LandingNav';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data, error, loading, resetPassword } = useResetPassword();
  const [superseded, setSuperseded] = useState(false);
  const token = searchParams.get('token');
  const { isInitializing } = useContext(AuthContext);

  const validation = useMemo(() => validatePasswordComplexity(password), [password]);
  const match = password && confirm && password === confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) return;
    if (!validation.valid) return;
    if (!match) return;
    const resp = await resetPassword(token, password);
    if (resp?.error && `${resp.error}`.toLowerCase().includes('superseded')) {
      setSuperseded(true);
      return;
    }
    if (resp?.success || !error) {
      setTimeout(() => navigate('/login'), 500);
    }
  };

  // Show loading spinner while fetching settings from cached token
  if (isInitializing) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-primary, #0f172a)',
        zIndex: 2000
      }}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <LandingNav />
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 80px)',
        padding: '20px'
      }}>
        <Container style={{ maxWidth: '520px', width: '100%' }}>
          <Card className="bg-dark text-light border-secondary" style={{ boxShadow: '0 12px 30px rgba(0,0,0,0.35)', borderRadius: 12 }}>
            <Card.Header as="h5" className="bg-dark text-light border-secondary">Reset Password</Card.Header>
            <Card.Body className="bg-dark text-light" style={{ padding: '2.5rem' }}>
              {!token && (
                <Alert variant="danger" className="bg-dark text-light border-danger">Missing or invalid reset token.</Alert>
              )}
              {superseded && (
                <Alert variant="warning" className="bg-dark text-light border-warning" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <ExclamationTriangleFill color="#f39c12" size={18} />
                  <span>This reset link has expired due to a more recent reset request. Please use the latest email.</span>
                </Alert>
              )}
              {error && !superseded && (
                <Alert variant="danger" className="bg-dark text-light border-danger">{error}</Alert>
              )}
              <Form onSubmit={handleSubmit} style={{ marginTop: '30px', paddingBottom: '1rem' }}>
                <Form.Group controlId="formPassword" className="mb-4">
                  <Form.Label>New Password</Form.Label>
                  <div style={{ position: 'relative' }}>
                    <Form.Control 
                      type={showPassword ? 'text' : 'password'} 
                      value={password} 
                      onChange={e => setPassword(e.target.value)}
                      required
                      className="bg-dark text-light"
                      style={{ backgroundColor: 'var(--dark-bg-input)', borderColor: 'var(--accent)', outline: 'none', boxShadow: 'none', paddingRight: '2.5rem' }}
                    />
                    <button
                      type="button"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword(prev => !prev)}
                      style={{
                        position: 'absolute',
                        right: 10,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        color: 'var(--text-muted, #adb5bd)',
                        cursor: 'pointer'
                      }}
                    >
                      {showPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                          <path d="M13.359 11.238C15.06 9.88 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.242.242-.494.476-.752.698l-.057.049.999.448z"/>
                          <path d="M11.297 9.176a3.5 3.5 0 0 0-4.473-4.473l.823.823a2.5 2.5 0 0 1 2.827 2.827l.823.823z"/>
                          <path d="M3.35 5.47c-.258.222-.51.456-.752.698A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.88 12.5 8 12.5c.716 0 1.39-.132 2.02-.36l.77.771A7.028 7.028 0 0 1 8 13.5C3 13.5 0 8 0 8s.94-1.88 2.641-3.238l.708.708z"/>
                          <path d="M5.354 7.475a2.5 2.5 0 0 0 3.172 3.172l.708.708a3.5 3.5 0 0 1-4.588-4.588l.708.708z"/>
                          <path d="M13.646 14.354l-12-12 .708-.708 12 12-.708.708z"/>
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                          <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8z"/>
                          <path d="M8 5.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5z"/>
                        </svg>
                      )}
                    </button>
                  </div>
                  {!validation.valid && password.length > 0 && (
                    <Form.Text style={{ color: '#f39c12' }}>{validation.message}</Form.Text>
                  )}
                </Form.Group>
                <Form.Group controlId="formConfirm" className="mb-4">
                  <Form.Label>Confirm Password</Form.Label>
                  <div style={{ position: 'relative' }}>
                    <Form.Control 
                      type={showConfirm ? 'text' : 'password'} 
                      value={confirm} 
                      onChange={e => setConfirm(e.target.value)}
                      required
                      className="bg-dark text-light"
                      style={{ backgroundColor: 'var(--dark-bg-input)', borderColor: 'var(--accent)', outline: 'none', boxShadow: 'none', paddingRight: '2.5rem' }}
                    />
                    <button
                      type="button"
                      aria-label={showConfirm ? 'Hide password confirmation' : 'Show password confirmation'}
                      onClick={() => setShowConfirm(prev => !prev)}
                      style={{
                        position: 'absolute',
                        right: 10,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        color: 'var(--text-muted, #adb5bd)',
                        cursor: 'pointer'
                      }}
                    >
                      {showConfirm ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                          <path d="M13.359 11.238C15.06 9.88 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.242.242-.494.476-.752.698l-.057.049.999.448z"/>
                          <path d="M11.297 9.176a3.5 3.5 0 0 0-4.473-4.473l.823.823a2.5 2.5 0 0 1 2.827 2.827l.823.823z"/>
                          <path d="M3.35 5.47c-.258.222-.51.456-.752.698A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.88 12.5 8 12.5c.716 0 1.39-.132 2.02-.36l.77.771A7.028 7.028 0 0 1 8 13.5C3 13.5 0 8 0 8s.94-1.88 2.641-3.238l.708.708z"/>
                          <path d="M5.354 7.475a2.5 2.5 0 0 0 3.172 3.172l.708.708a3.5 3.5 0 0 1-4.588-4.588l.708.708z"/>
                          <path d="M13.646 14.354l-12-12 .708-.708 12 12-.708.708z"/>
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                          <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8z"/>
                          <path d="M8 5.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5z"/>
                        </svg>
                      )}
                    </button>
                  </div>
                  {confirm.length > 0 && !match && (
                    <Form.Text style={{ color: '#f39c12' }}>Passwords do not match</Form.Text>
                  )}
                </Form.Group>
                
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '30px' }}>
                  <button 
                    type="submit" 
                    disabled={loading || !token || !validation.valid || !match}
                    style={{
                      background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
                      color: '#fff',
                      border: 'none',
                      fontSize: '1.1em',
                      padding: '12px 28px',
                      borderRadius: '50px',
                      fontWeight: '600',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      boxShadow: '0 2px 8px rgba(139, 92, 246, 0.2)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: (loading || !token || !validation.valid || !match) ? 'not-allowed' : 'pointer',
                      opacity: (loading || !token || !validation.valid || !match) ? 0.7 : 1,
                      minWidth: '180px'
                    }}
                    onMouseEnter={e => {
                      if (!(loading || !token || !validation.valid || !match)) {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.3)';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!(loading || !token || !validation.valid || !match)) {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 8px rgba(139, 92, 246, 0.2)';
                      }
                    }}
                  >
                    {loading ? <Spinner animation="border" size="sm" /> : 'Update Password'}
                  </button>
                </div>
              </Form>
              
              <div style={{ 
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: '24px',
                paddingTop: '20px',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <small className="text-muted">
                  Remember your password?{' '}
                  <Link to="/login" className="text-decoration-none" style={{ color: 'var(--accent)' }}>
                    Back to Login
                  </Link>
                </small>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </div>
    </>
  );
};

export default ResetPassword;


